/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, {
  ChangeEvent,
  FormEvent,
  FC,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LoginAction } from "./ducks/slices";
import { requestPasswordReset } from "./ducks/operations";
import {
  ErrorMessage,
  ErrorMessageValidator,
  LaddaButton,
  NestedErrorMessage,
} from "@advapacs/common";
import { useAppDispatch } from "../../config/store";
import { useSelector } from "react-redux";
import { IRootState } from "../../config/reducers";
import getUpdatePasswordValidator from "@src/utils/validator";

const ForgotPasswordForm: FC = () => {
  const { t } = useTranslation("login");
  const [, setError] = useState({});
  const validator = useMemo(() => {
    return getUpdatePasswordValidator(t);
  }, [t]);
  const [stateForm, setStateForm] = useState({
    username: "",
  });
  const { passwordResetErrorMessage, passwordResetComplete } = useSelector(
    (state: IRootState) => state.loginReducer
  );
  const dispatch = useAppDispatch();
  const [loading] = useState(false);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setStateForm({ ...stateForm, [name]: value });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!stateForm.username) return;
    if (!validator.allValid()) {
      validator.showMessages();
      setError({});
      return;
    }
    dispatch(requestPasswordReset(stateForm?.username));
  };

  useEffect(() => {
    return () => {
      dispatch(LoginAction.clearPasswordReset());
    };
  }, [dispatch]);

  const errorMessage = useMemo(() => {
    if ((passwordResetErrorMessage as NestedErrorMessage)?.errors) {
      return (passwordResetErrorMessage as NestedErrorMessage)?.errors[0]
        ?.message;
    }
    return (passwordResetErrorMessage as ErrorMessage)?.message;
  }, [passwordResetErrorMessage]);
  return (
    <>
      <form onSubmit={submit}>
        <h5 className="text-center mb-3">{t("reset_password")}</h5>
        {passwordResetComplete && !passwordResetErrorMessage && (
          <div className="text-center">{t("email_reset_confirmation")}</div>
        )}
        {!passwordResetComplete && (
          <>
            <div className="form-group form-group-feedback form-group-feedback-left m-b-none">
              <input
                type="text"
                className="form-control"
                placeholder={t("email")}
                name="username"
                onChange={handleInputChange}
                value={stateForm.username}
              />
              <div className="form-control-feedback">
                <i className="icon-user text-muted" />
              </div>
            </div>

            {errorMessage && (
              <div className="form-group m-t-xs m-b-none">
                <div className="alert bg-danger m-b-xs text-white alert-styled-left error-container">
                  {errorMessage}
                </div>
              </div>
            )}
            <ErrorMessageValidator
              errorMessage={validator.message(
                t("email"),
                stateForm.username,
                "required"
              )}
            />
          </>
        )}
        <div className="form-group m-t-xs">
          {!passwordResetComplete && (
            <LaddaButton
              className="btn btn-primary btn-block btn-ladda btn-ladda-spinner"
              onClickHandler={submit}
              type="button"
              btnName={t("send_password_reset_instructions")}
              disabled={loading || !stateForm.username}
            />
          )}

          <Link className="btn btn-light mt-2 btn-block btn-ladda" to="/">
            <i className="fa fa-angle-double-left ml-2" />
            {t("back_to_login")}
          </Link>
        </div>
      </form>
    </>
  );
};
export default ForgotPasswordForm;
